import { Subscribable, SubscribableOrNullableValue } from "knockout";
import WCCSliderAdaptiveItem from "../item/adaptive/adaptive.model";
import WCCSliderItem from "../item/base/base.model";
import WCCSliderPage from "../models/page";
import WCCSliderBaseLayout, { WCCSliderBaseLayoutConfig } from "./base";

export interface WCCSliderAdaptiveLayoutContext<T> {
    page: WCCSliderPage<T, WCCSliderAdaptiveItem<T>>
    pageWidth: number
}

export interface WCCSliderAdaptiveLayoutConfig {
    offset?: SubscribableOrNullableValue<number>
}

export interface WCCSliderAdaptiveLayoutConfigFull<T> extends WCCSliderBaseLayoutConfig<T, WCCSliderAdaptiveItem<T>>, WCCSliderAdaptiveLayoutConfig { }

export default class WCCSliderAdaptiveLayout<T> extends WCCSliderBaseLayout<T, WCCSliderAdaptiveItem<T>, WCCSliderAdaptiveLayoutContext<T>> {
    private offset: Subscribable<number | undefined>

    itemWidth: Subscribable<number>    

    constructor(config: WCCSliderAdaptiveLayoutConfigFull<T>) {
        super(config);

        this.offset = ko.flattenComputed(config.offset);

        this.itemWidth = this.items.pluck(item => item.width).average();       

        queueMicrotask(() => {
            this.effects.register([
                ko.computed(() => this.updateSpaces()).extend({ deferred: true })
            ]);
        });
    }

    protected getContext(pages: WCCSliderPage<T, WCCSliderAdaptiveItem<T>>[], page: WCCSliderPage<T, WCCSliderAdaptiveItem<T>>, isFinal = false) {
        const pageWidth = this.getPageWidth({
            isFirst: page === pages[0],
            isLast: isFinal && page === pages[pages.length - 1]
        });

        return {
            page: page,
            pageWidth: pageWidth
        };
    }

    protected buildItem(data: T) {
        return new WCCSliderAdaptiveItem({ data });
    }

    protected canAddItem(item: WCCSliderItem<T>, { page, pageWidth }: WCCSliderAdaptiveLayoutContext<T>): boolean {
        const itemOffset = this.offset();
        const totalOffset = itemOffset != undefined ? itemOffset * (page.allItems().length + 1) : 0;

        return page.width() + page.borrowedWidth() + totalOffset + item.width() <= pageWidth;
    }

    private updateSpaces() {
        const page = this.activePage();
        const hasMultiplePages = this.pages().length > 1;
        const offset = this.offset();

        for (const item of this.items()) {
            item.paddingLeft(0);
            item.paddingRight(0);
        }

        if (page != undefined && (hasMultiplePages || offset != undefined)) {
            const context = this.getContext(this.pages(), page, true);
            const pageWidth = context.pageWidth;

            const freeSpace = pageWidth - page.width() - page.borrowedWidth();
            const pageItems = page.allItems();

            if (freeSpace > 0 && pageItems.length > 1) {
                let spaceToAddToItem = 0,
                    spaceToAddToBoundaries = 0;

                if (offset != undefined) {
                    spaceToAddToItem = offset;
                    spaceToAddToBoundaries = freeSpace - offset * (page.allItems().length - 1);
                } else {
                    spaceToAddToItem = freeSpace / (page.allItems().length - 1)
                }

                for (const item of pageItems) {
                    item.paddingLeft(spaceToAddToItem / 2);
                    item.paddingRight(spaceToAddToItem / 2);
                }

                _.first(pageItems)?.paddingLeft(spaceToAddToBoundaries / 2);
                _.last(pageItems)?.paddingRight(spaceToAddToBoundaries / 2);
            }
        }
    }
}